import React from "react";
import PropTypes from "prop-types";
import { defaultTemplate } from "core/hoc";
import { PreviewLayout } from "core/layouts";
import { ApplicationEmptyState, BasicCheckbox, BaasicDropdown, BaasicButton, BaasicModal, BaasicDraggableModal, GlobalSignalRListener } from "core/components";
import { observer } from "mobx-react";
import {
  NikoPatientInformationBar,
  NikoPatientEligibilityStatusNoteModal,
  NikoPatientInsuranceQuestionModal,
  NikoPatientInsuranceInfoNoteModal,
  NikoTemplateCreateModal,
  NikoPatientStatusModal,
  NikoTemplateMissingProductNoteModal,
  NikoTemplateMissingDocumentNoteModal,
  NikoPatientTemplateItemCalculationModal,
  PatientTemplateClinicalStatusNoteModal,
} from "application/niko-patient/components";
import { NikoPatientTemplateList } from "application/niko-patient/pages";
import { FacilityOrderTemplateNewFlowDeleteModal, FacilityOrderNewFlowConfirmModal, FacilityOrderTemplateItemNewFlowDeleteQuestionModal } from "application/facility-order-new-flow/components";
import { Button } from "@progress/kendo-react-buttons";
import { FacilityOrderTemplatePNMissingInformationModal } from "application/facility-order/components";
import { NikoFacilityOrderConfirmationModal, NikoFacilityOrderReasonForChangeModal } from "application/niko-facility-order/components";
import { Tooltip as ReactTooltip } from "react-tooltip";

const TooltipContent = observer(({ initianValue, history, fieldName }) => {
  if (!history || history.fieldName !== fieldName) return null;
  const date = new Date(history.eventDate + "Z");
  const formattedDate = date.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).replace(',', '');
  return (<div>
    <div>Previous: {initianValue ? initianValue : 'blank'}</div>
    <div style={{ fontSize: '11px', opacity: 0.75 }}>{history ? history.userName + ', ' + formattedDate : '-'}</div>
  </div>);
});

const NikoPatientPreviewTemplate = function ({ nikoPatientPreviewViewStore }) {
  const {
    loaderStore,
    nikoPatientDetail,
    statusStore,
    disabled,
    changePatientStatus,
    showInsuranceInfo,
    changeInsuranceInfoVisibility,
    updateInsuranceInfoNote,
    eligibilityStatusStore,
    insuranceInfoNote,
    changeEligibilityStatus,
    nikoPatientEligibilityStatusNoteModal,
    openNikoPatientEligibilityStatusNoteModal,
    nikoPatientInsuranceQuestionModal,
    openNikoPatientInsuranceQuestionModal,
    nikoPatientInsuranceInfoNoteModal,
    openNikoPatientInsuranceInfoNoteModal,
    eligibleEligibilityStatusId,
    nikoTemplateCreateModal,
    selectedTab,
    setTab,
    tabItems,
    facilityOrderTemplateNewFlowDeleteModal,
    nikoPatientStatusModal,
    isFacilityEditMode,
    facilityStore,
    changeFacilityEditMode,
    tempFacility,
    setFacility,
    changeFacility,
    facilityEditConfirmationModal,
    refreshKey,
    patientInfoKey,
    nikoTemplateMissingProductNoteModal,
    facilityOrderTemplateItemNewFlowDeleteQuestionModal,
    nikoTemplateMissingDocumentNoteModal,
    patientTemplateClinicalStatusNoteModal,
    templatePNMissingInformationModal,
    nikoPatientTemplateItemCalculationModal,
    patientTemplateDoNotSupplyModal,
    openReasonForChangeModal,
    nikoFacilityOrderReasonForChangeModal,
    toggleRentalsCapped,
    toggleHasIvpole,
    signalRAction,
    showPreviousFlag,
    getFieldHistory,
    rootStore,
  } = nikoPatientPreviewViewStore;

  const renderTabContent = () => {
    switch (selectedTab) {
      case "Orders":
        return (
          <div className="card--tertiary__niko">
            <div className="u-pad--med">
              <h4>Orders content coming soon...</h4>
            </div>
          </div>
        );
      case "History":
        return (
          <div className="card--tertiary__niko">
            <div className="u-pad--med">
              <h4>History content coming soon...</h4>
            </div>
          </div>
        );
      default:
        return (
          <div className="card--tertiary__niko">
            <NikoPatientTemplateList pageViewStore={nikoPatientPreviewViewStore} key={refreshKey} />
          </div>
        );
    }
  };

  const tabButtons = tabItems.map((item) => (
    <BaasicButton
      key={item.id}
      className={`tabs__item__niko ${item.id === selectedTab ? "active" : ""} ${item.disabled ? "tabs__item__niko--disabled" : ""}`}
      label={item.name}
      onClick={() => {
        if (item.disabled) {
          return;
        }
        setTab(item.id);
      }}
      disabled={item.disabled}
      showTooltip={false}
    />
  ));

  return (
    <PreviewLayout
      className="container container--new-flow"
      store={nikoPatientPreviewViewStore}
      emptyRenderer={<ApplicationEmptyState />}
      layoutFooterVisible={false}
      loading={loaderStore.loading}
    >
      {nikoPatientDetail && <GlobalSignalRListener groupId={nikoPatientDetail.facilityId} rootStore={rootStore} action={signalRAction} />}
      {!loaderStore.loading && (
        <React.Fragment>
          <div className="u-mar--y--xlrg u-flex--primary is-nested">
            {nikoPatientDetail.isMasterPatient &&
              <i className="u-icon u-icon--sml u-icon--new-facility u-icon--new-facility--primary u-mar--right--sml" />}
            <h1>{nikoPatientDetail.nikoPatient.lastName + ", " + nikoPatientDetail.nikoPatient.firstName}</h1>
            {nikoPatientDetail.inactiveDate && (
              <ReactTooltip positionStrategy="fixed"
                id="inactiveDateTooltip"
                place="left"
                key={nikoPatientDetail.inactiveDate} // force re-render when date changes
              />
            )}
            <div
              className={`u-flex--default u-mar--left--auto ${showPreviousFlag(nikoPatientDetail) && !nikoPatientDetail.isNew ? "k-dropdown--status-indicator-opacity-50" : ""}`}
              {...(nikoPatientDetail.inactiveDate && {
                "data-tooltip-content": new Date(nikoPatientDetail.inactiveDate).toLocaleDateString("en-US", {
                  year: "2-digit",
                  month: "2-digit",
                  day: "2-digit",
                }),
                "data-tooltip-id": "inactiveDateTooltip",
              })}
            >
              <BaasicDropdown
                store={statusStore}
                disabled={disabled}
                value={nikoPatientDetail.status}
                onChange={(e) => changePatientStatus(e.value)}
              />
              {showPreviousFlag(nikoPatientDetail) && !nikoPatientDetail.isNew &&
                <React.Fragment>
                  <ReactTooltip positionStrategy="fixed" id={"registerPatientStatusTip" + nikoPatientDetail.id} place="top" delayShow={600} render={() =>
                    <TooltipContent initianValue={nikoPatientDetail.initialNikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail.Status} history={nikoPatientDetail.history} fieldName={"Status"} />} />
                  <div className="k-dropdown--tooltip" data-tooltip-id={"registerPatientStatusTip" + nikoPatientDetail.id} onMouseEnter={async () => await getFieldHistory(nikoPatientDetail, 'Status', nikoPatientDetail.status)} data-tooltip-content=""></div>
                </React.Fragment>
              }
            </div>
          </div>
          <div className="card--tertiary u-mar--bottom--sml--alt">
            <div className="u-flex--default card--tertiary__header">
              <div className="patient__group">
                <div className="u-flex--primary u-flex--no-wrap">
                  <i className="u-icon u-icon--sml u-icon--new-facility u-icon--new-facility--primary u-mar--right--sml" />
                  {isFacilityEditMode ?
                    <BaasicDropdown store={facilityStore} disabled={disabled} value={tempFacility} onChange={(e) => { setFacility(e.value) }} />
                    :
                    <h4 className="card--tertiary__header__title">{nikoPatientDetail.facility && nikoPatientDetail.facility.name}</h4>
                  }
                </div>
              </div>
              {isFacilityEditMode ?
                <div className="u-flex--primary u-flex--no-wrap u-flex--gap--1-5">
                  <BaasicButton
                    onlyIcon={true}
                    showTooltip={false}
                    className="btn btn--icon ex-hover"
                    onClick={() => changeFacility()}
                    disabled={disabled}
                    icon={"u-icon u-icon--lrg u-icon--patient-check"}
                  />
                  <BaasicButton
                    onlyIcon={true}
                    showTooltip={false}
                    className="btn btn--icon ex-hover"
                    icon="u-icon u-icon--lrg u-icon--patient-close"
                    onClick={() => changeFacilityEditMode()}
                    disabled={disabled}
                  />
                </div>
                :
                <BaasicButton
                  onlyIcon={true}
                  showTooltip={false}
                  className="btn btn--icon is-expand"
                  icon="u-icon u-icon--med u-icon--edit-alt--primary"
                  onClick={() => changeFacilityEditMode()}
                  disabled={disabled}
                />
              }
              <NikoPatientInformationBar key={patientInfoKey} nikoPatient={nikoPatientDetail.nikoPatient} disabled={disabled} />
              <div className="u-flex--default u-flex--gap--1-5 u-mar--left--auto" style={{ alignItems: 'center' }}>
                <span className="type--color--secondary type--sml">Has IV Pole</span>
                <BasicCheckbox id={nikoPatientDetail.id + '-ivpole'} checked={nikoPatientDetail.nikoPatient.hasIvpole} onChange={() => toggleHasIvpole()} />
                <span className="type--color--secondary type--sml">Rentals Capped</span>
                <BasicCheckbox id={nikoPatientDetail.id} checked={nikoPatientDetail.nikoPatient.rentalsCapped} onChange={() => toggleRentalsCapped()} />
              </div>
            </div>
            <div className="u-flex--primary card--tertiary__niko">
              <h4 className="type--wgt--medium">Insurance Info</h4>
              <div onClick={() => changeInsuranceInfoVisibility()} className="content__header__btn">
                <i className={` ${showInsuranceInfo ? "" : "u-rotate--180"} u-mar--left--xsml u-icon u-icon--med u-icon--arrow-up--primary`}></i>
              </div>
            </div>
            {showInsuranceInfo && (
              <div className="btn--group__wrapper card--tertiary__niko">
                <div className="card--tertiary__flex">
                  <div className="card--tertiary__data">
                    <BaasicDropdown
                      className="k-dropdown--primary k-dropdown--nowrap has-empty"
                      store={eligibilityStatusStore}
                      value={nikoPatientDetail.eligibilityStatusId}
                      onChange={(e) => {
                        changeEligibilityStatus(e.value);
                      }}
                      disabled={disabled}
                    />
                    {nikoPatientDetail.eligibilityStatusId && nikoPatientDetail.eligibilityStatusId != eligibleEligibilityStatusId && (
                      <React.Fragment>
                        <ReactTooltip positionStrategy="fixed" id={"nikoPatientEligibilityStatusNoteTooltip"} place="bottom" />
                        <Button
                          className="btn btn--link"
                          icon="u-icon u-icon--med u-icon--us-note"
                          onClick={() => openNikoPatientEligibilityStatusNoteModal()}
                          disabled={disabled}
                          data-tooltip-id={"nikoPatientEligibilityStatusNoteTooltip"}
                          data-tooltip-content="Eligibility Status Note"
                        />
                      </React.Fragment>
                    )}
                  </div>
                  <div className="card--tertiary__data u-flex--grow--1">
                    <div>
                      <div className="card--tertiary__data__item">
                        <div>Primary:</div>
                        <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.primaryActive.insurance.current && nikoPatientDetail.primaryActive.insurance.current.name}</div>
                        {nikoPatientDetail.primaryActive.policyNo.current && <div>{nikoPatientDetail.primaryActive.policyNo.current}</div>}
                      </div>
                      {nikoPatientDetail.primaryInactive.insurance.current &&
                        <div className="card--tertiary__data__item">
                          <div>Inactive:</div>
                          <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.primaryInactive.insurance.current.name}</div>
                          <div>{nikoPatientDetail.primaryInactive.policyNo.current && nikoPatientDetail.primaryInactive.policyNo.current}</div>
                        </div>
                      }
                    </div>
                    <div>
                      <div className="card--tertiary__data__item">
                        <div>Secondary:</div>
                        <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.secondaryActive.insurance.current && nikoPatientDetail.secondaryActive.insurance.current.name}</div>
                        {nikoPatientDetail.secondaryActive.policyNo.current && <div>{nikoPatientDetail.secondaryActive.policyNo.current}</div>}
                      </div>
                      {nikoPatientDetail.secondaryInactive.insurance.current &&
                        <div className="card--tertiary__data__item">
                          <div>Inactive:</div>
                          <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.secondaryInactive.insurance.current.name}</div>
                          <div>{nikoPatientDetail.secondaryInactive.policyNo.current && nikoPatientDetail.secondaryInactive.policyNo.current}</div>
                        </div>
                      }
                    </div>
                    {nikoPatientDetail.insuranceQuestion && (
                      <div className="u-flex u-flex--default u-flex--gap--2">
                        <div className="card--tertiary__header__divider"></div>
                        <ReactTooltip positionStrategy="fixed" id={"nikoPatientRedInsuranceQuestion1Tooltip"} place="bottom" />
                        <Button
                          className="btn btn--status--red u-mar--left--xsml"
                          icon="u-icon u-icon--med-lrg u-icon--us-question-note--red"
                          onClick={() => {
                            openNikoPatientInsuranceQuestionModal();
                          }}
                          disabled={disabled}
                          data-tooltip-id={"nikoPatientRedInsuranceQuestion1Tooltip"}
                          data-tooltip-content="Insurance Question"
                        />
                      </div>
                    )}
                    <div className="u-flex u-flex--default u-align--h--right u-mar--right--med">
                      <div className="btn--group status--sqare__wrapper">
                        {!nikoPatientDetail.insuranceQuestion && (
                          <React.Fragment>
                            <ReactTooltip positionStrategy="fixed" id={"nikoPatientInsuranceQuestionTooltip"} place="bottom" />
                            <Button
                              className="btn no-hover"
                              icon="u-icon u-icon--med-lrg u-icon--us-question-note-sh"
                              onClick={() => openNikoPatientInsuranceQuestionModal()}
                              disabled={disabled}
                              data-tooltip-id={"nikoPatientInsuranceQuestionTooltip"}
                              data-tooltip-content="Insurance Question"
                            />
                          </React.Fragment>
                        )}
                        {!nikoPatientDetail.insuranceInfoNote && (
                          <React.Fragment>
                            <ReactTooltip positionStrategy="fixed" id={"nikoPatientInsuranceInfoNoteTooltip"} place="bottom" />
                            <Button
                              className="btn no-hover"
                              icon="u-icon u-icon--med-lrg u-icon--us-note"
                              onClick={() => openNikoPatientInsuranceInfoNoteModal()}
                              disabled={disabled}
                              data-tooltip-id={"nikoPatientInsuranceInfoNoteTooltip"}
                              data-tooltip-content="Insurance Info Note"
                            />
                          </React.Fragment>
                        )}
                        <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--us-edit" onClick={() => openReasonForChangeModal()} disabled={disabled} data-tooltip-content="Edit Insurances" data-tooltip-id={"nikoPatientInsuranceInfoNoteTooltip"} />
                      </div>
                    </div>
                  </div>
                </div>
                {nikoPatientDetail.insuranceInfoNote && (
                  <div className="c-message has-input c-message--note u-mar--top--sml">
                    <i className="u-icon u-icon--sml u-icon--add-note" />
                    <input
                      className="input input--sml input--message u-flex--grow--1 type--sml"
                      onClick={(e) => e.target.select()}
                      onChange={(e) => {
                        updateInsuranceInfoNote(e.target.value);
                      }}
                      onBlur={(e) => {
                        updateInsuranceInfoNote(e.target.value, true);
                      }}
                      value={insuranceInfoNote}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="card--tertiary__niko">
              {/* Jira ISG-2774: Tabs temporary replaced with title. Uncomment tabs and delete title to revert. */}
              {/* <div className="tabs_niko_div">{tabButtons}</div> */}
              <h4 className="type--wgt--medium">Templates</h4>
              {renderTabContent()}
            </div>
          </div>
        </React.Fragment>
      )}

      <BaasicModal modalParams={nikoPatientEligibilityStatusNoteModal} className="modal__content modal--med" >
        <NikoPatientEligibilityStatusNoteModal />
      </BaasicModal >
      < BaasicDraggableModal modalParams={nikoPatientInsuranceQuestionModal} className="modal__content modal--med" >
        <NikoPatientInsuranceQuestionModal />
      </BaasicDraggableModal >
      <BaasicModal modalParams={nikoPatientInsuranceInfoNoteModal} className="modal__content modal--med">
        <NikoPatientInsuranceInfoNoteModal />
      </BaasicModal>
      <BaasicModal modalParams={nikoTemplateCreateModal} className="modal__content modal--xxlrg">
        <NikoTemplateCreateModal />
      </BaasicModal>
      <BaasicModal modalParams={facilityOrderTemplateNewFlowDeleteModal} className="modal__content modal--med">
        <FacilityOrderTemplateNewFlowDeleteModal />
      </BaasicModal>
      <BaasicDraggableModal modalParams={nikoTemplateMissingProductNoteModal} className="modal__content modal--med">
        <NikoTemplateMissingProductNoteModal />
      </BaasicDraggableModal>
      <BaasicModal modalParams={nikoPatientStatusModal} className="modal__content modal--med">
        <NikoPatientStatusModal />
      </BaasicModal>
      <BaasicModal modalParams={facilityEditConfirmationModal} className="modal__content modal--sml">
        <FacilityOrderNewFlowConfirmModal />
      </BaasicModal>
      <BaasicModal modalParams={facilityOrderTemplateItemNewFlowDeleteQuestionModal} className="modal__content modal--med">
        <FacilityOrderTemplateItemNewFlowDeleteQuestionModal />
      </BaasicModal>
      <BaasicDraggableModal modalParams={patientTemplateDoNotSupplyModal} className="modal__content modal--med">
        <NikoFacilityOrderConfirmationModal />
      </BaasicDraggableModal>
      <BaasicModal modalParams={nikoTemplateMissingDocumentNoteModal} className="modal__content modal--med">
        <NikoTemplateMissingDocumentNoteModal />
      </BaasicModal>
      <BaasicModal modalParams={patientTemplateClinicalStatusNoteModal} className="modal__content modal--med">
        <PatientTemplateClinicalStatusNoteModal />
      </BaasicModal>
      <BaasicModal modalParams={templatePNMissingInformationModal} className="modal__content modal--med">
        <FacilityOrderTemplatePNMissingInformationModal />
      </BaasicModal>
      <BaasicDraggableModal modalParams={nikoPatientTemplateItemCalculationModal} className="modal__content modal--lrg">
        <NikoPatientTemplateItemCalculationModal />
      </BaasicDraggableModal>
      <BaasicModal modalParams={nikoFacilityOrderReasonForChangeModal} className="modal__content modal--lrg">
        <NikoFacilityOrderReasonForChangeModal />
      </BaasicModal>
    </PreviewLayout>
  );
};

NikoPatientPreviewTemplate.propTypes = {
  nikoPatientPreviewViewStore: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  t: PropTypes.func,
};

export default defaultTemplate(NikoPatientPreviewTemplate);
